import type {
  MessageContentType,
  ParticipantKind,
  ParticipantSummary,
  ThreadBlock,
  ThreadBlockChild,
  ThreadBlockKind,
  ThreadBlockStatus,
  ThreadDetail,
  ThreadMessage,
  ThreadWorkflowStatus,
} from "skedyul-ui/ui";

type CallParticipantSummary = NonNullable<
  ThreadBlock["callSession"]
>["participants"][number];
type CallSessionCommunicationChannel = NonNullable<
  ThreadBlock["callSession"]
>["communicationChannel"];

function evaluationFromStructured(
  structured: Record<string, unknown> | null | undefined,
): ThreadMessage["evaluation"] {
  if (!structured) return null;
  const evaluation = structured.evaluation;
  if (
    evaluation &&
    typeof evaluation === "object" &&
    !Array.isArray(evaluation)
  ) {
    const record = evaluation as Record<string, unknown>;
    return {
      score: typeof record.score === "number" ? record.score : null,
      passed: record.passed === true,
      toolError: record.toolError === true,
    };
  }
  if (typeof structured.score !== "number") return null;
  return {
    score: structured.score,
    passed: structured.passed === true,
    toolError: structured.toolError === true,
  };
}

function toAgentKind(
  value: string | null | undefined,
): ParticipantSummary["agentKind"] {
  if (
    value === "SYSTEM" ||
    value === "APP" ||
    value === "WORKSPACE" ||
    value === "MODEL"
  ) {
    return value;
  }
  return null;
}

function toParticipantSummary(input: {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  kind: string;
  contactId?: string | null;
  agentKind?: string | null;
}): ParticipantSummary {
  return {
    id: input.id,
    displayName: input.displayName,
    avatarUrl: input.avatarUrl,
    kind: input.kind as ParticipantKind,
    contactId: input.contactId ?? null,
    agentKind: toAgentKind(input.agentKind),
  };
}

function toCommunicationChannel(input: {
  id: string;
  name: string;
  icon?: string | null;
}): CallSessionCommunicationChannel {
  return {
    id: input.id,
    name: input.name,
    ...(input.icon ? { icon: input.icon } : {}),
  };
}

function toCallParticipantSummary(input: {
  id: string;
  participant: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    kind: string;
    contactId?: string | null;
    agentKind?: string | null;
  };
  status: string;
  joinedAt: string | null;
  leftAt: string | null;
  isMuted?: boolean;
  isVideoOn?: boolean;
  phoneNumber?: string | null;
  trackLabel?: string | null;
}): CallParticipantSummary {
  return {
    id: input.id,
    participant: toParticipantSummary(input.participant),
    status: input.status as CallParticipantSummary["status"],
    joinedAt: input.joinedAt,
    leftAt: input.leftAt,
    isMuted: input.isMuted ?? false,
    isVideoOn: input.isVideoOn ?? false,
    phoneNumber: input.phoneNumber ?? null,
    trackLabel: input.trackLabel ?? null,
  };
}

type CreatedRealtimeThreadLink = {
  id: string;
  role: string;
  status?: string;
  purpose?: string | null;
  reason?: string | null;
  sourceThread?: { id: string; title: string | null };
  targetThread?: { id: string; title: string | null };
};

export type RealtimeCreatedBlockInput = {
  id: string;
  kind: string;
  sequence: number;
  status: string | null;
  content: string | null;
  contentStructured?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  requiresApproval?: boolean;
  parentBlockId: string | null;
  participantId?: string | null;
  messageId?: string | null;
  agentRunId?: string | null;
  workflowRunId?: string | null;
  subThreadId?: string | null;
  threadLinkId?: string | null;
  createdAt: string;
  workflowRun?: {
    id: string;
    handle: string;
    status: string;
    workflowName: string;
    workflowRunId: string;
  } | null;
  subThread?: {
    id: string;
    title: string | null;
    kind?: string;
    status: string;
    messageCount?: number;
    participants?: Array<{
      id: string;
      displayName: string;
      avatarUrl: string | null;
      kind: string;
    }>;
  } | null;
  threadLink?: CreatedRealtimeThreadLink | null;
  data?: {
    description?: string;
    appHandle?: string;
    appName?: string;
    appIconUrl?: string | null;
    sourceMessageId?: string | null;
    message?: RealtimeCreatedBlockInput["message"];
    sender?: NonNullable<RealtimeCreatedBlockInput["message"]>["sender"];
    subThread?: {
      id: string;
      title: string | null;
      status: string;
      lastMessageAt?: string | null;
      lastMessagePreview?: string | null;
    };
    threadLink?: CreatedRealtimeThreadLink;
    workflowRun?: {
      id: string;
      workflowRunId: string | null;
      status: string;
      workflow?: { handle?: string; name?: string | null };
    };
  } | null;
  message?: {
    id: string;
    content: string;
    contentRaw: string | null;
    contentType: string;
    aiGenerated: boolean;
    createdAt?: string;
    actualSenderAddress?: string | null;
    sender: {
      id: string;
      displayName: string;
      avatarUrl: string | null;
      kind: string;
    } | null;
    attachments: Array<{
      id: string;
      fileId?: string;
      fileName: string;
      fileUrl: string | null;
      fileType: string | null;
      fileSize: number;
    }>;
  } | null;
  participant?: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    kind: string;
  } | null;
  agentRun?: {
    id: string;
    agentId: string | null;
    agentName: string | null;
  } | null;
  callSession?: RealtimeCallSessionInput | null;
};

export type RealtimeCallSessionInput = {
  id: string;
  kind: string;
  status: string;
  startedAt: string | null;
  endedAt?: string | null;
  duration?: number | null;
  summary?: string | null;
  initiator?: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    kind: string;
    memberId?: string | null;
    contactId?: string | null;
    agentId?: string | null;
    agentKind?: string | null;
  };
  communicationChannel?: {
    id: string;
    name: string;
    icon: string | null;
  };
  participants?: Array<{
    id: string;
    participant: {
      id: string;
      displayName: string;
      avatarUrl: string | null;
      kind: string;
      memberId?: string | null;
      contactId?: string | null;
      agentId?: string | null;
      agentKind?: string | null;
    };
    status: string;
    joinedAt: string | null;
    leftAt: string | null;
    isMuted?: boolean;
    isVideoOn?: boolean;
    phoneNumber?: string | null;
    trackLabel?: string | null;
  }>;
  transcriptMessages?: unknown[];
  lastTranscriptMessage?: RealtimeCallTranscriptMessageInput | null;
};

export type RealtimeCallTranscriptMessageInput = {
  id: string;
  content: string;
  contentType: string;
  createdAt: string;
  aiGenerated: boolean;
  sendStatus: string | null;
  sender: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    kind: string;
    memberId?: string | null;
    contactId?: string | null;
    agentId?: string | null;
    agentKind?: string | null;
  };
};

export type RealtimeBlockUpdatedInput = {
  threadId: string;
  blockId: string;
  status?: string | null;
  content?: string | null;
  contentStructured?: Record<string, unknown> | null;
  approvedById?: string | null;
  approvedAt?: string | null;
  rejectedById?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  metadata?: Record<string, unknown> | null;
  message?: {
    id: string;
    content: string;
    contentRaw: string | null;
    contentType: string;
    aiGenerated: boolean;
    sender: {
      id: string;
      displayName: string | null;
      avatarUrl: string | null;
      kind: string;
    } | null;
    attachments: unknown[];
  } | null;
  callSession?: Partial<RealtimeCallSessionInput> | null;
};

export function extractSourceMessageIdFromCreatedBlock(
  block: RealtimeCreatedBlockInput,
): string | null {
  const sourceMessageId = block.data?.sourceMessageId;
  return typeof sourceMessageId === "string" && sourceMessageId.length > 0
    ? sourceMessageId
    : null;
}

export function extractWorkflowRunIdFromCreatedBlock(
  block: RealtimeCreatedBlockInput,
): string | null {
  if (block.workflowRun?.workflowRunId) {
    return block.workflowRun.workflowRunId;
  }
  const fromData = block.data?.workflowRun?.workflowRunId;
  return typeof fromData === "string" && fromData.length > 0 ? fromData : null;
}

export function mapWorkflowRunFromCreatedBlock(
  block: RealtimeCreatedBlockInput,
): {
  id: string;
  handle: string;
  status: ThreadWorkflowStatus;
  workflowName: string;
  workflowRunId: string;
} | null {
  if (block.workflowRun) {
    return {
      id: block.workflowRun.id,
      handle: block.workflowRun.handle,
      status: block.workflowRun.status as ThreadWorkflowStatus,
      workflowName: block.workflowRun.workflowName,
      workflowRunId: block.workflowRun.workflowRunId,
    };
  }

  const wr = block.data?.workflowRun;
  if (!wr?.workflowRunId) return null;

  return {
    id: wr.id,
    handle: wr.workflow?.handle ?? wr.id,
    status: wr.status as ThreadWorkflowStatus,
    workflowName: wr.workflow?.name ?? "Workflow",
    workflowRunId: wr.workflowRunId,
  };
}

export function mapSubThreadFromCreatedBlock(
  block: RealtimeCreatedBlockInput,
): {
  id: string;
  title: string | null;
  kind: "CONVERSATION" | "INTERNAL" | "ESCALATION" | "BROADCAST";
  status: "OPEN" | "RESOLVED" | "ARCHIVED";
  sourceMessageId: string | null;
} | null {
  const fromData = block.data?.subThread;
  const fromRelation = block.subThread;
  const structured = block.contentStructured;
  const virtualSubThread =
    structured?.isSandboxPreview && structured.subThread
      ? (structured.subThread as {
          id: string;
          title?: string | null;
          status?: string;
        })
      : null;
  const id = fromData?.id ?? fromRelation?.id ?? virtualSubThread?.id;
  if (!id) return null;

  return {
    id,
    title:
      fromData?.title ?? fromRelation?.title ?? virtualSubThread?.title ?? null,
    kind: (fromRelation?.kind ?? "CONVERSATION") as
      | "CONVERSATION"
      | "INTERNAL"
      | "ESCALATION"
      | "BROADCAST",
    status: (fromData?.status ??
      fromRelation?.status ??
      virtualSubThread?.status ??
      "OPEN") as "OPEN" | "RESOLVED" | "ARCHIVED",
    sourceMessageId:
      block.data?.sourceMessageId != null &&
      block.data.sourceMessageId.length > 0
        ? block.data.sourceMessageId
        : null,
  };
}

export function mapSubThreadBlockRelation(block: RealtimeCreatedBlockInput) {
  const mapped = mapSubThreadFromCreatedBlock(block);
  if (!mapped) return null;

  const structured = block.contentStructured;
  const previewMessages =
    structured?.isSandboxPreview &&
    structured.sandboxPreview &&
    typeof structured.sandboxPreview === "object"
      ? (structured.sandboxPreview as { messages?: unknown[] }).messages
          ?.length ?? 0
      : undefined;

  return {
    id: mapped.id,
    title: mapped.title,
    kind: mapped.kind,
    status: mapped.status,
    messageCount: block.subThread?.messageCount ?? previewMessages ?? 0,
    participants: (block.subThread?.participants ?? []).map((p) => ({
      id: p.id,
      displayName: p.displayName,
      avatarUrl: p.avatarUrl,
      kind: p.kind as "CONTACT" | "MEMBER" | "AGENT" | "SYSTEM" | "WORKFLOW",
    })),
  };
}

function mapThreadLinkFromCreatedBlock(block: RealtimeCreatedBlockInput) {
  const link = block.threadLink ?? block.data?.threadLink;
  if (!link?.sourceThread || !link.targetThread) return null;

  return {
    id: link.id,
    role: link.role as "CONSULTATION" | "ESCALATION" | "HANDOFF" | "REFERENCE",
    status: link.status ?? "ACTIVE",
    purpose: link.purpose ?? link.reason ?? null,
    sourceThread: {
      id: link.sourceThread.id,
      title: link.sourceThread.title,
    },
    targetThread: {
      id: link.targetThread.id,
      title: link.targetThread.title,
    },
  };
}

type RealtimeMessagePayload = NonNullable<RealtimeCreatedBlockInput["message"]>;
type RealtimeSender = NonNullable<RealtimeMessagePayload["sender"]>;

function resolveSenderFromBlock(
  block: RealtimeCreatedBlockInput,
  message: RealtimeMessagePayload,
): RealtimeSender | null {
  if (message.sender) {
    return message.sender;
  }

  const data = block.data as
    | { sender?: RealtimeSender | null }
    | null
    | undefined;
  if (data?.sender) {
    return data.sender;
  }

  if (block.participant) {
    return {
      id: block.participant.id,
      displayName: block.participant.displayName,
      avatarUrl: block.participant.avatarUrl,
      kind: block.participant.kind,
    };
  }

  return null;
}

export function mapRealtimeAttachment(att: {
  id: string;
  fileId?: string;
  fileName: string;
  fileUrl: string | null;
  fileType: string | null;
}) {
  return {
    id: att.id,
    fileId: att.fileId,
    name: att.fileName,
    mimeType: att.fileType,
    url: att.fileUrl ?? "",
  };
}

function realtimeMessageId(
  message: { id?: string | null } | null | undefined,
): string {
  return typeof message?.id === "string" ? message.id.trim() : "";
}

function resolveRealtimeMessage(
  block: RealtimeCreatedBlockInput,
): RealtimeMessagePayload | null {
  if (realtimeMessageId(block.message)) {
    return {
      ...block.message!,
      sender: resolveSenderFromBlock(block, block.message!),
    };
  }

  if (block.kind !== "MESSAGE" || !block.data) {
    return null;
  }

  const data = block.data as {
    message?: RealtimeMessagePayload | null;
    sender?: RealtimeMessagePayload["sender"];
  };

  if (!realtimeMessageId(data.message)) {
    return null;
  }

  return {
    ...data.message!,
    sender: resolveSenderFromBlock(block, data.message!),
  };
}

function mapRealtimeTranscriptMessage(
  message: RealtimeCallTranscriptMessageInput,
): ThreadMessage {
  return {
    id: message.id,
    content: message.content,
    contentType: message.contentType as MessageContentType,
    createdAt: message.createdAt,
    aiGenerated: message.aiGenerated,
    sendStatus: message.sendStatus as ThreadMessage["sendStatus"],
    sender: toParticipantSummary(message.sender),
  };
}

export function mapCallSessionFromRealtime(
  callSession: RealtimeCallSessionInput,
): NonNullable<ThreadBlock["callSession"]> {
  return {
    id: callSession.id,
    kind: callSession.kind as NonNullable<ThreadBlock["callSession"]>["kind"],
    status: callSession.status as NonNullable<
      ThreadBlock["callSession"]
    >["status"],
    startedAt: callSession.startedAt,
    endedAt: callSession.endedAt ?? null,
    duration: callSession.duration ?? null,
    summary: callSession.summary ?? null,
    lastTranscriptMessage: callSession.lastTranscriptMessage
      ? mapRealtimeTranscriptMessage(callSession.lastTranscriptMessage)
      : null,
    initiator: callSession.initiator
      ? toParticipantSummary(callSession.initiator)
      : toParticipantSummary({
          id: "",
          displayName: "Unknown",
          avatarUrl: null,
          kind: "SYSTEM",
        }),
    communicationChannel: callSession.communicationChannel
      ? toCommunicationChannel(callSession.communicationChannel)
      : toCommunicationChannel({ id: "", name: "Phone" }),
    participants: (callSession.participants ?? []).map(
      toCallParticipantSummary,
    ),
    transcriptMessages: callSession.lastTranscriptMessage
      ? [mapRealtimeTranscriptMessage(callSession.lastTranscriptMessage)]
      : [],
  };
}

export function mergeCallSessionFromRealtime(
  existing: ThreadBlock["callSession"] | null | undefined,
  patch: Partial<RealtimeCallSessionInput>,
): NonNullable<ThreadBlock["callSession"]> | null {
  if (!existing && !patch.id) {
    return null;
  }

  const base =
    existing ?? mapCallSessionFromRealtime(patch as RealtimeCallSessionInput);
  const nextLastTranscriptMessage =
    patch.lastTranscriptMessage !== undefined
      ? patch.lastTranscriptMessage
        ? mapRealtimeTranscriptMessage(patch.lastTranscriptMessage)
        : null
      : base.lastTranscriptMessage ?? null;
  const transcriptMessages = (() => {
    const existingMessages = existing?.transcriptMessages ?? [];
    if (!patch.lastTranscriptMessage) {
      return existingMessages;
    }
    const mapped = mapRealtimeTranscriptMessage(patch.lastTranscriptMessage);
    if (existingMessages.some((message) => message.id === mapped.id)) {
      return existingMessages;
    }
    return [...existingMessages, mapped];
  })();

  return {
    ...base,
    ...(patch.status != null
      ? { status: patch.status as typeof base.status }
      : {}),
    ...(patch.startedAt !== undefined ? { startedAt: patch.startedAt } : {}),
    ...(patch.endedAt !== undefined ? { endedAt: patch.endedAt ?? null } : {}),
    ...(patch.duration !== undefined
      ? { duration: patch.duration ?? null }
      : {}),
    ...(patch.summary !== undefined ? { summary: patch.summary ?? null } : {}),
    ...(patch.lastTranscriptMessage !== undefined
      ? { lastTranscriptMessage: nextLastTranscriptMessage }
      : {}),
    ...(patch.initiator
      ? { initiator: toParticipantSummary(patch.initiator) }
      : {}),
    ...(patch.communicationChannel
      ? {
          communicationChannel: toCommunicationChannel(
            patch.communicationChannel,
          ),
        }
      : {}),
    ...(patch.participants
      ? { participants: patch.participants.map(toCallParticipantSummary) }
      : {}),
    transcriptMessages,
  };
}

export function mapRealtimeCreatedBlockToThreadBlock(
  threadId: string,
  block: RealtimeCreatedBlockInput,
): ThreadBlock {
  const resolvedMessage = resolveRealtimeMessage(block);
  const { contentStructured, ...blockRest } = block;

  return {
    ...blockRest,
    contentStructured: contentStructured ?? null,
    metadata: block.metadata ?? null,
    threadId,
    kind: block.kind as ThreadBlockKind,
    status: block.status as ThreadBlockStatus | null,
    updatedAt: block.createdAt,
    approvalTimeoutMs: null,
    expiresAt: null,
    approvedById: null,
    approvedAt: null,
    approvedBy: null,
    rejectedById: null,
    rejectedAt: null,
    rejectedBy: null,
    rejectionReason: null,
    participant: block.participant
      ? {
          id: block.participant.id,
          displayName: block.participant.displayName,
          avatarUrl: block.participant.avatarUrl,
          kind: block.participant.kind as ParticipantKind,
        }
      : null,
    message: resolvedMessage
      ? {
          id: resolvedMessage.id,
          content: resolvedMessage.content,
          contentRaw: resolvedMessage.contentRaw,
          contentType: resolvedMessage.contentType as MessageContentType,
          aiGenerated: resolvedMessage.aiGenerated,
          actualSenderAddress: resolvedMessage.actualSenderAddress ?? null,
          sender: resolvedMessage.sender
            ? {
                id: resolvedMessage.sender.id,
                displayName: resolvedMessage.sender.displayName,
                avatarUrl: resolvedMessage.sender.avatarUrl,
                kind: resolvedMessage.sender.kind as ParticipantKind,
              }
            : null,
          attachments: (resolvedMessage.attachments || []).map(
            mapRealtimeAttachment,
          ),
          evaluation: evaluationFromStructured(contentStructured ?? null),
        }
      : null,
    agentRun: block.agentRun
      ? {
          id: block.agentRun.id,
          agentId: block.agentRun.agentId,
          agentName: block.agentRun.agentName,
        }
      : null,
    workflowRun: mapWorkflowRunFromCreatedBlock(block),
    subThread: mapSubThreadBlockRelation(block),
    threadLink: mapThreadLinkFromCreatedBlock(block),
    callSession: block.callSession
      ? mapCallSessionFromRealtime(block.callSession)
      : null,
    childBlocks: [],
  };
}

export function mapRealtimeBlockUpdatedPayload(
  blockData: RealtimeBlockUpdatedInput,
  existingBlock?: ThreadBlock | null,
): Partial<ThreadBlock> {
  const updates: Partial<ThreadBlock> = {
    status: blockData.status as ThreadBlockStatus | null | undefined,
    ...(blockData.content !== undefined ? { content: blockData.content } : {}),
    ...(blockData.contentStructured !== undefined
      ? { contentStructured: blockData.contentStructured }
      : {}),
    approvedAt: blockData.approvedAt,
    rejectedAt: blockData.rejectedAt,
    rejectionReason: blockData.rejectionReason,
    ...(blockData.metadata !== undefined
      ? { metadata: blockData.metadata }
      : {}),
  };

  const incomingMessageId = realtimeMessageId(blockData.message);
  if (incomingMessageId && blockData.message) {
    updates.message = {
      ...blockData.message,
      contentType: blockData.message.contentType as MessageContentType,
      sender: blockData.message.sender
        ? {
            ...blockData.message.sender,
            kind: blockData.message.sender.kind as ParticipantKind,
            displayName: blockData.message.sender.displayName ?? "",
          }
        : null,
      attachments: blockData.message.attachments as Array<{
        id: string;
        fileId?: string;
        name: string;
        mimeType: string | null;
        url: string;
      }>,
      evaluation:
        evaluationFromStructured(blockData.contentStructured) ??
        existingBlock?.message?.evaluation ??
        null,
    };
  } else if (!realtimeMessageId(existingBlock?.message)) {
    // A draft has no ThreadMessage. Clear an empty-id placeholder so the
    // timeline does not keep painting it as delivered.
    updates.message = null;
  }

  if (blockData.callSession) {
    updates.callSession = mergeCallSessionFromRealtime(
      existingBlock?.callSession,
      blockData.callSession,
    );
  }

  return updates;
}

function appendBlockToBlocks(
  blocks: ThreadBlock[],
  block: ThreadBlock,
): ThreadBlock[] {
  const messageId = block.message?.id;
  if (messageId) {
    const optimisticIndex = blocks.findIndex(
      (b) =>
        (b as ThreadBlock & { _messageId?: string })._messageId === messageId ||
        (b.message?.id === messageId &&
          (b as ThreadBlock & { _optimistic?: boolean })._optimistic),
    );
    if (optimisticIndex !== -1) {
      const next = [...blocks];
      next[optimisticIndex] = block;
      return next;
    }
  }

  if (blocks.some((b) => b.id === block.id)) {
    return blocks;
  }

  if (block.parentBlockId) {
    return blocks.map((parentBlock) => {
      if (parentBlock.id !== block.parentBlockId) {
        return parentBlock;
      }
      const childExists = parentBlock.childBlocks?.some(
        (c) => c.id === block.id,
      );
      if (childExists) {
        return parentBlock;
      }
      const childBlocks = [...(parentBlock.childBlocks ?? [])];
      childBlocks.push({
        id: block.id,
        kind: block.kind,
        sequence: block.sequence,
        status: block.status,
        content: block.content,
        contentStructured: block.contentStructured,
        createdAt: block.createdAt,
        durationMs: null,
        participant: block.participant,
        agentRun: block.agentRun,
      });
      return { ...parentBlock, childBlocks };
    });
  }

  const insertIndex = blocks.findIndex((b) => b.sequence > block.sequence);
  if (insertIndex === -1) {
    return [...blocks, block];
  }
  const next = [...blocks];
  next.splice(insertIndex, 0, block);
  return next;
}

function updateBlockInBlocks(
  blocks: ThreadBlock[],
  blockId: string,
  updates: Partial<ThreadBlock>,
): ThreadBlock[] {
  const blockIndex = blocks.findIndex((b) => b.id === blockId);
  if (blockIndex !== -1) {
    const existingBlock = blocks[blockIndex];
    if (!existingBlock) return blocks;
    const next = [...blocks];
    next[blockIndex] = { ...existingBlock, ...updates } as ThreadBlock;
    return next;
  }

  for (let i = 0; i < blocks.length; i++) {
    const parentBlock = blocks[i];
    if (!parentBlock?.childBlocks) continue;

    const childIndex = parentBlock.childBlocks.findIndex(
      (c) => c.id === blockId,
    );
    if (childIndex === -1) continue;

    const existingChild = parentBlock.childBlocks[childIndex];
    if (!existingChild) return blocks;

    const newChildBlocks = [...parentBlock.childBlocks];
    newChildBlocks[childIndex] = {
      ...existingChild,
      ...updates,
    } as ThreadBlockChild;

    const next = [...blocks];
    next[i] = { ...parentBlock, childBlocks: newChildBlocks };
    return next;
  }

  return blocks;
}

export function appendBlockToThreadDetail(
  thread: ThreadDetail,
  block: ThreadBlock,
): ThreadDetail {
  const blocks = thread.blocks ?? [];
  return {
    ...thread,
    blocks: appendBlockToBlocks(blocks, block),
  };
}

export function updateBlockInThreadDetail(
  thread: ThreadDetail,
  blockId: string,
  updates: Partial<ThreadBlock>,
): ThreadDetail {
  const blocks = thread.blocks ?? [];
  return {
    ...thread,
    blocks: updateBlockInBlocks(blocks, blockId, updates),
  };
}

type OptimisticThreadBlock = ThreadBlock & {
  _optimistic?: boolean;
  _messageId?: string;
};

function isOptimisticBlock(block: ThreadBlock): boolean {
  const optimistic = block as OptimisticThreadBlock;
  return (
    optimistic._optimistic === true ||
    typeof optimistic._messageId === "string" ||
    block.id.startsWith("optimistic-")
  );
}

function getBlockMessageId(block: ThreadBlock): string | null {
  const optimistic = block as OptimisticThreadBlock;
  return optimistic._messageId ?? block.message?.id ?? null;
}

/** Soft refresh: keep blocks absent from the server page, update overlaps, append new. */
export function mergeBlocksFromServer(
  prevBlocks: ThreadBlock[],
  serverBlocks: ThreadBlock[],
): ThreadBlock[] {
  if (serverBlocks.length === 0) {
    return prevBlocks;
  }

  const serverById = new Map(serverBlocks.map((block) => [block.id, block]));
  const serverByMessageId = new Map<string, ThreadBlock>();
  for (const serverBlock of serverBlocks) {
    const messageId = serverBlock.message?.id;
    if (messageId && !serverByMessageId.has(messageId)) {
      serverByMessageId.set(messageId, serverBlock);
    }
  }

  const seenBlockIds = new Set<string>();
  const seenMessageIds = new Set<string>();
  const merged: ThreadBlock[] = [];

  for (const prev of prevBlocks) {
    const prevMessageId = getBlockMessageId(prev);
    const fromServerById = serverById.get(prev.id);
    const fromServerByMessage =
      prevMessageId != null ? serverByMessageId.get(prevMessageId) : undefined;

    // Optimistic local blocks use synthetic ids (optimistic-*); reconcile by message id.
    if (isOptimisticBlock(prev) && fromServerByMessage) {
      if (seenBlockIds.has(fromServerByMessage.id)) {
        continue;
      }
      merged.push(fromServerByMessage);
      seenBlockIds.add(fromServerByMessage.id);
      const confirmedMessageId = fromServerByMessage.message?.id;
      if (confirmedMessageId) {
        seenMessageIds.add(confirmedMessageId);
      }
      continue;
    }

    if (fromServerById) {
      if (seenBlockIds.has(fromServerById.id)) {
        continue;
      }
      merged.push(fromServerById);
      seenBlockIds.add(fromServerById.id);
      const confirmedMessageId = fromServerById.message?.id;
      if (confirmedMessageId) {
        seenMessageIds.add(confirmedMessageId);
      }
      continue;
    }

    // Soft merge already upgraded optimistic → real block id; drop duplicate by message id.
    if (
      prevMessageId &&
      seenMessageIds.has(prevMessageId) &&
      prev.kind === "MESSAGE"
    ) {
      continue;
    }

    if (seenBlockIds.has(prev.id)) {
      continue;
    }

    merged.push(prev);
    seenBlockIds.add(prev.id);
    if (prevMessageId && prev.kind === "MESSAGE") {
      seenMessageIds.add(prevMessageId);
    }
  }

  for (const serverBlock of serverBlocks) {
    if (seenBlockIds.has(serverBlock.id)) {
      continue;
    }
    const messageId = serverBlock.message?.id;
    if (
      messageId &&
      serverBlock.kind === "MESSAGE" &&
      seenMessageIds.has(messageId)
    ) {
      continue;
    }
    merged.push(serverBlock);
    seenBlockIds.add(serverBlock.id);
    if (messageId && serverBlock.kind === "MESSAGE") {
      seenMessageIds.add(messageId);
    }
  }

  merged.sort((a, b) => {
    if (a.sequence !== b.sequence) {
      return a.sequence - b.sequence;
    }
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  return merged;
}

export function parseThreadBlockCreatedEvent(data: unknown): {
  threadId: string;
  block: RealtimeCreatedBlockInput;
} | null {
  const payload = data as {
    payload?: { threadId: string; block: RealtimeCreatedBlockInput };
  };
  const blockData =
    payload.payload ??
    (data as { threadId: string; block: RealtimeCreatedBlockInput });
  if (!blockData.threadId || !blockData.block) {
    return null;
  }
  return blockData;
}

export function parseThreadBlockUpdatedEvent(
  data: unknown,
): RealtimeBlockUpdatedInput | null {
  const payload = data as { payload?: RealtimeBlockUpdatedInput };
  const blockData = payload.payload ?? (data as RealtimeBlockUpdatedInput);
  if (!blockData.threadId || !blockData.blockId) {
    return null;
  }
  return blockData;
}
